import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CandidateDetailsCard } from 'modules/hiring/shared/components';
import { INTERVIEW_STEPS } from 'modules/hiring/shared/constants';
import { useCandidate } from 'modules/hiring/shared/hooks';
import { rejectCandidate } from 'modules/hiring/shared/service';
import { CollapsibleCard, Loader } from 'modules/shared/components';
import { logError } from 'utils/logger';
import { InterviewContent, InterviewStepper } from '../components';
import {
  completeEmployeeOnboarding,
  getCandidateInterviewDetails,
  updateCandidateInterviewDetails,
} from '../interview.service';
import {
  buildInterviewUpdatePayload,
  getStepIndexByKey,
  hasAcceptedOffer,
  resolveActiveStepFromKey,
} from '../utils';

const OFFER_STEP_KEY = 'offerLetter';
const ONBOARDING_STEP_KEY = 'onboarding';

export default function InterviewFlow() {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { candidate, loading } = useCandidate(applicationId);

  const [loadingInterview, setLoadingInterview] = useState(false);
  const [interviewData, setInterviewData] = useState(null);

  const stepKeyFromUrl = searchParams.get('step');
  const viewStep = resolveActiveStepFromKey(
    INTERVIEW_STEPS,
    stepKeyFromUrl,
    interviewData?.currentInterviewRound || 0,
  );
  const safeViewStep = Math.min(viewStep, interviewData?.currentInterviewRound || 0);

  const fetchInterviewDetails = useCallback(async () => {
    if (!applicationId) return;
    setLoadingInterview(true);
    try {
      const data = await getCandidateInterviewDetails(applicationId);
      const offerAccepted = hasAcceptedOffer(data.round3);
      const offerStepIndex = getStepIndexByKey(INTERVIEW_STEPS, OFFER_STEP_KEY);
      const onboardingStepIndex = getStepIndexByKey(INTERVIEW_STEPS, ONBOARDING_STEP_KEY);
      if (
        offerAccepted &&
        data.currentInterviewRound === offerStepIndex &&
        onboardingStepIndex > offerStepIndex
      ) {
        const payload = { ...data, currentInterviewRound: onboardingStepIndex };
        await updateCandidateInterviewDetails(applicationId, payload);
        data.currentInterviewRound = onboardingStepIndex;
      }

      setInterviewData(data);
    } catch (error) {
      toast.error(
        logError({
          error: error,
          message: 'Failed to fetch candidate details',
          code: 'CANDIDATE_DETAILS_FETCH_FAILED',
        }),
      );
    } finally {
      setLoadingInterview(false);
    }
  }, [applicationId]);

  useEffect(() => {
    fetchInterviewDetails();
  }, [fetchInterviewDetails]);

  const handleApprove = async (roundPatch) => {
    setLoadingInterview(true);
    const messageMap = {
      round1: 'Candidate scores updated successfully',
      round2: 'Candidate scores updated successfully',
      round3: 'Candidate scores updated successfully',
      'onboarding-share': 'Personal data entry form shared successfully',
      'onboarding-submit': 'Candidate details updated successfully',
      documentCollection: 'Documents submitted successfully',
      contract: 'Contract shared successfully',
      induction: 'Induction completed successfully',
    };

    const errorMessageMap = {
      round1: 'Failed to update candidate scores',
      round2: 'Failed to update candidate scores',
      round3: 'Failed to update candidate scores',
      'onboarding-share': 'Failed to share personal data entry form',
      onboarding: 'Failed to update candidate details',
      documentCollection: 'Failed to submit documents',
      contract: 'Failed to share contract',
      induction: 'Failed to complete induction',
    };

    const messageKey =
      roundPatch.action === 'share'
        ? `${roundPatch.currentRoundKey}-share`
        : roundPatch.currentRoundKey;

    try {
      let payload = buildInterviewUpdatePayload(interviewData, roundPatch, INTERVIEW_STEPS);

      await updateCandidateInterviewDetails(applicationId, payload);

      if (roundPatch.action === 'complete' && roundPatch.credentials) {
        const { credentials } = roundPatch;

        if (!credentials?.employeeId || !credentials?.password) {
          throw new Error('Employee credentials missing');
        }

        await completeEmployeeOnboarding(applicationId, credentials);
      }

      toast.success(messageMap[messageKey] || 'Interview approved');
      navigate('/candidates?status=scheduled');
    } catch (error) {
      toast.error(
        logError({
          error: error,
          message: errorMessageMap[messageKey] || 'Failed to approve interview',
          code: 'INTERVIEW_APPROVE_FAILED',
        }),
      );
    } finally {
      setLoadingInterview(false);
    }
  };

  const handleReject = async (roundPatch) => {
    setLoadingInterview(true);
    try {
      const payload = buildInterviewUpdatePayload(interviewData, roundPatch, INTERVIEW_STEPS);

      await updateCandidateInterviewDetails(applicationId, payload);
      await rejectCandidate(applicationId, { reason: roundPatch.rejectionReason });
      toast.success('Candidate rejected');
      navigate('/candidates?status=rejected');
    } catch (error) {
      toast.error(
        logError({
          error: error,
          message: 'Failed to reject candidate',
          code: 'CANDIDATE_REJECT_FAILED',
        }),
      );
    } finally {
      setLoadingInterview(false);
    }
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex > (interviewData?.currentInterviewRound || 0)) return;

    const stepKey = INTERVIEW_STEPS[stepIndex].key;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('step', stepKey);
        return next;
      },
      { replace: true },
    );
  };

  if (loading || loadingInterview) return <Loader />;

  if (!candidate) return <Alert severity='error'>Candidate not found. Please try again</Alert>;

  return (
    <>
      <CollapsibleCard
        title={`${candidate.name} details`}
        defaultExpanded
        meta={
          <a
            href={`http://www.sdllp.in/contact?tab=careers&id=${applicationId}`}
            className='underline row'
          >
            <Icon icon='solar:link-bold' />
            Job posting link
          </a>
        }
      >
        <CandidateDetailsCard candidate={candidate} />
      </CollapsibleCard>

      <InterviewStepper
        currentRound={interviewData?.currentInterviewRound}
        onStepClick={handleStepClick}
        status={candidate?.status}
      />
      <InterviewContent
        currentRound={interviewData?.currentInterviewRound}
        viewedRound={safeViewStep}
        interviewData={interviewData}
        candidateData={candidate}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </>
  );
}
